import { CheckBox, Spin } from 'lego-on-react';
import React, { FC, ReactNode, useState } from 'react';

import block from 'propmods';

import { FormFieldContext } from '../../context/formFieldContext';
import VerificationError from '../Settings/VerificationError';

import Caption from './Caption';
import { FormError } from './FormError';

import './Form.scss';

const b = block('Form');

interface FormProps {
    error?: string;
    onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
    innerRef?: React.Ref<HTMLFormElement>;
}

const Form: React.SFC<FormProps> = ({ children, error, onSubmit, innerRef }) => (
    <form
        {...b({ invalid: Boolean(error) })}
        onSubmit={(event) => {
            if (onSubmit) {
                event.preventDefault();
                onSubmit(event);
                return false;
            }
        }}
        ref={innerRef}
    >
        {children}
    </form>
);

interface HeaderProps {
    title?: string;
    subtitle?: string | ReactNode;
    caption?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ title, subtitle, children, caption }) => (
    <header {...b('header', { 'with-caption': Boolean(caption) })}>
        <h2>{title || children}</h2>
        {subtitle && <div {...b('header-subtitle')}>{subtitle}</div>}
        {caption && <Caption>{caption}</Caption>}
    </header>
);

interface FieldProps {
    caption?: React.ReactNode;
    sideCaption?: React.ReactNode;
    description?: React.ReactElement;
    documentationUrl?: string;
    error?: string;
    height?: number;
    label?: string | React.ReactNode;
    name?: string;
    progress?: boolean;
    required?: boolean;
    rtl?: boolean;
    verificationError?: string;
    theme?: 'self-centered';
    wide?: boolean;
    disableBodyOffset?: boolean;
}

const Field: React.FC<FieldProps> = ({
    caption,
    children,
    documentationUrl,
    error,
    height,
    label,
    name,
    progress,
    required,
    rtl,
    description,
    verificationError,
    sideCaption,
    theme,
    wide,
    disableBodyOffset,
}) => {
    const [nonValidationError, setNonValidationError] = useState<React.ReactNode>('');
    const displayedError = error || nonValidationError;

    return (
        <FormFieldContext.Provider value={{ nonValidationError, setNonValidationError }}>
            <div
                {...b('field', {
                    invalid: Boolean(displayedError),
                    needToVerify: Boolean(verificationError),
                    name,
                    theme,
                })}
            >
                {label && (
                    <div {...b('label', { required })}>
                        <label htmlFor={name}>{label}</label>
                        {sideCaption ? <Caption offsetTop={false}>{sideCaption}</Caption> : null}
                    </div>
                )}
                <div {...b('fieldBody', { wide, lifted: !disableBodyOffset, rtl })} style={{ height }}>
                    {children}
                    {progress && <Spin size="xxs" progress />}
                    {displayedError && <FormError offsetTop>{displayedError}</FormError>}
                    {caption && <Caption url={documentationUrl}>{caption}</Caption>}
                    {verificationError && <VerificationError {...b('VerificationError')} text={verificationError} />}
                    {description}
                </div>
            </div>
        </FormFieldContext.Provider>
    );
};

interface SubfieldProps {
    error?: string;
    height?: number;
    progress?: boolean;
}

const Subfield: React.SFC<SubfieldProps> = ({ children, error, height, progress }) => {
    const [nonValidationError, setNonValidationError] = useState<React.ReactNode>('');
    const displayedError = error || nonValidationError;

    return (
        <FormFieldContext.Provider value={{ nonValidationError, setNonValidationError }}>
            <div {...b('subfield', { invalid: Boolean(displayedError) })} style={{ height }}>
                {children}
                {progress && <Spin size="xxs" progress />}
                {displayedError && <FormError offsetTop>{displayedError}</FormError>}
            </div>
        </FormFieldContext.Provider>
    );
};

interface CheckboxFieldProps {
    label?: string;
    name?: string;
    checked?: boolean;
    disabled?: boolean;
    onChange?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    caption?: React.ReactNode;
    description?: React.ReactElement;
    documentationUrl?: string;
    multiline?: boolean;
    htmlTitle?: string;
}

const CheckboxField: FC<CheckboxFieldProps> = ({
    label,
    name,
    caption,
    documentationUrl,
    multiline,
    description,
    ...checkboxProps
}) => {
    return (
        <Field
            label={label}
            caption={caption}
            documentationUrl={documentationUrl}
            name={name}
            theme={multiline ? 'self-centered' : undefined}
            description={description}
        >
            <FormCheckbox multiline={multiline} {...checkboxProps} />
        </Field>
    );
};

interface FormCheckboxProps {
    multiline?: boolean;
    centered?: boolean;
    disabled?: boolean;
    htmlTitle?: string;
    checked?: boolean;
    onChange?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
    children,
    multiline,
    disabled,
    htmlTitle,
    checked,
    onChange,
    centered = true,
}) => {
    return (
        <label {...b('checkbox-wrapper', { multiline, disabled, centered })} title={htmlTitle}>
            <CheckBox
                theme="normal"
                size="m"
                tone="default"
                view="default"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
            />
            {children}
        </label>
    );
};

export { Form, Header, Field, Subfield, CheckboxField };
